import { ApolloProvider } from '@apollo/client';
import { PersistGate } from 'redux-persist/integration/react';
import { Provider } from 'react-redux';
import { Elements } from '@stripe/react-stripe-js';
import { SemanticToastContainer } from 'react-semantic-toasts';
import { loadStripe } from '@stripe/stripe-js/pure';
import { createBrowserHistory } from 'history';
import * as Sentry from '@sentry/react';
import PropTypes from 'prop-types';
import React from 'react';
import { TranslationProvider, ThemeProvider } from '@playbookux/playbook-web-core';
import 'semantic-ui-css/semantic.min.css';
import 'react-semantic-toasts/styles/react-semantic-alert.css';
import 'font-awesome/css/font-awesome.css';

/* eslint-disable import/no-unresolved */
import useStore from 'playbook-web/store';

import client from './core/services/graphql';
import WithSentry from './core/components/with-sentry';
import Intercom from './core/components/intercom';
import Routes from './routes';
import { persistor, store } from './store';

import translations from './core/i18n/localization/translations.json';
import { config } from './config';
import './scripts';

let stripePromise;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(config.REACT_APP_STRIPE_API_KEY);
  }
  return stripePromise;
};

const history = createBrowserHistory();

const PlatformComponent = (props) => {
  if (config.REACT_APP_PLATFORM_TYPE === 'company') {
    return (
      <Intercom>
        <Elements stripe={getStripe()}>
          {props.children}
        </Elements>
      </Intercom>
    );
  }

  return <>{props.children}</>;
};

const StoreComponent = (props) => {
  store.subscribe(() => {
    const state = store.getState();
    const { authentication: { token, scope } = {} } = state;
    useStore.setState({ token, scope });
  });

  return (
    <>{props.children}</>
  );
};

StoreComponent.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

const Root = () => (
  <WithSentry history={history}>
    <Provider store={store}>
      <StoreComponent>
        <PersistGate persistor={persistor}>
          <ThemeProvider>
            <TranslationProvider source={translations}>
              <ApolloProvider client={client.getClient()}>
                <PlatformComponent>
                  <Routes history={history} />
                </PlatformComponent>
              </ApolloProvider>
            </TranslationProvider>
          </ThemeProvider>
        </PersistGate>
      </StoreComponent>
    </Provider>
    <SemanticToastContainer className="toast" position="bottom-left" />
  </WithSentry>
);

export default Sentry.withProfiler(Root);

PlatformComponent.propTypes = {
  children: PropTypes.element.isRequired,
};
